import { useCallback, useEffect, useState } from "react";
import { IStandardsModalTab } from "@/search/Langston/types/IStandardsModalTab";
import { STANDARDS_SET_MODE } from "@/search/Langston/types/IStandardsSetMode";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema/LangstonParameterSchema";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import {
  useStandardsModalTabsNavigation,
  useStandardsSet,
  useStandardsSetFilter,
} from "./hooks";
import { StandardsModalContext } from "./StandardsModalProvider.context";
import {
  IStandardsModalContext,
  IStandardsModalProvider,
} from "./StandardsModalProvider.types";

const availableGrades = LangstonParameterSchema.grade_levels.values.map(
  ({ label }) => label,
);

const StandardsModalProvider = ({ children }: IStandardsModalProvider) => {
  const { allStandardsModalResults, standardAggregations } =
    useStandardsSearchContext();
  const [modalTabs, setModalTabs] = useState<IStandardsModalTab[]>([]);
  const [needle, setNeedle] = useState<string>("");

  const {
    newStandardsSet,
    setNewStandardsSet,
    createdStandardsSets,
    addNewStandardsSet,
    editStandardsSet,
    deleteStandardsSet,
    standardsSetTitleError,
    setStandardsSetTitleError,
    isEditingSet,
    setIsEditingSet,
    standardsSetMode,
    setStandardsSetMode,
    confirmModalData,
    setConfirmModalData,
    isConfirmActionModalOpen,
    setIsConfirmActionModalOpen,
    openConfirmModal,
    closeConfirmModal,
    draftStandardsSetId,
    setDraftStandardsSetId,
    handleCreateDraftStandardsSet,
    operationErrorMessage,
    resetOperationErrorMessage,
    isPublishingStandardsSet,
    setIsPublishingStandardsSet,
    isUpdatingStandardsSet,
    setIsUpdatingStandardsSet,
    isDeletingStandardsSet,
    setIsDeletingStandardsSet,
  } = useStandardsSet();

  const {
    currentStandards,
    setCurrentStandards,
    filteredStandards,
    setFilteredStandards,
    selectedSubjectFilter,
    selectedGradeFilter,
    setSelectedSubjectFilter,
    setSelectedGradeFilter,
    selectedDateFilter,
    setSelectedDateFilter,
    availableDates,
    availableSubjects,
  } = useStandardsSetFilter(allStandardsModalResults, standardAggregations);

  const {
    switchEditDataOnTabChange,
    allowModalTabAutoChanges,
    setAllowModalTabAutoChanges,
  } = useStandardsModalTabsNavigation(
    createdStandardsSets,
    setConfirmModalData,
    modalTabs,
  );

  const handleApplyConfirmModal = useCallback(() => {
    const { setId } = confirmModalData;
    if (standardsSetMode === STANDARDS_SET_MODE.DELETE && setId) {
      deleteStandardsSet(setId);
    } else if (operationErrorMessage !== "") {
      resetOperationErrorMessage();
      setIsPublishingStandardsSet(false);
      setIsUpdatingStandardsSet(false);
      setIsDeletingStandardsSet(false);
      if (newStandardsSet.id === "") {
        handleCreateDraftStandardsSet();
      }
    }
    setIsConfirmActionModalOpen(false);
  }, [
    createdStandardsSets,
    standardsSetMode,
    confirmModalData,
    operationErrorMessage,
  ]);

  useEffect(() => {
    setCurrentStandards(allStandardsModalResults);
  }, [allStandardsModalResults]);

  useEffect(() => {
    if (operationErrorMessage !== "" && !isConfirmActionModalOpen) {
      setIsConfirmActionModalOpen(true);
    }
  }, [operationErrorMessage, isConfirmActionModalOpen]);

  const contextReturn: IStandardsModalContext = {
    getters: {
      currentStandards,
      newStandardsSet,
      createdStandardsSets,
      selectedSubjectFilter,
      selectedGradeFilter,
      selectedDateFilter,
      filteredStandards,
      availableDates,
      availableSubjects,
      availableGrades,
      isConfirmActionModalOpen,
      standardsSetMode,
      confirmModalData,
      isEditingSet,
      standardsSetTitleError,
      modalTabs,
      draftStandardsSetId,
      operationErrorMessage,
      isPublishingStandardsSet,
      isUpdatingStandardsSet,
      isDeletingStandardsSet,
      allowModalTabAutoChanges,
      needle,
    },
    handlers: {
      setCurrentStandards,
      setNewStandardsSet,
      addNewStandardsSet,
      editStandardsSet,
      deleteStandardsSet,
      setSelectedSubjectFilter,
      setSelectedGradeFilter,
      setSelectedDateFilter,
      setFilteredStandards,
      openConfirmModal,
      closeConfirmModal,
      setStandardsSetMode,
      setConfirmModalData,
      handleApplyConfirmModal,
      setIsEditingSet,
      setStandardsSetTitleError,
      switchEditDataOnTabChange,
      setModalTabs,
      setDraftStandardsSetId,
      handleCreateDraftStandardsSet,
      setAllowModalTabAutoChanges,
      setNeedle,
    },
  };

  return (
    <StandardsModalContext.Provider value={contextReturn}>
      {children}
    </StandardsModalContext.Provider>
  );
};

StandardsModalProvider.displayName = "StandardsModalProvider";
export { StandardsModalProvider };
